import { withRouter } from 'react-router-dom';
import React, {
    useState, useEffect, useCallback, useContext
} from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from 'gw-digital-auth-react';
import { useDataRefresh, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { Loader, useModal, ToastProvider } from '@jutro/components';
import { renderContentFromMetadataInternal } from '@jutro/uiconfig';
import { ClaimService } from 'gw-capability-claim';
import messages from './FNOLWizardPage_AX.messages';
import styles from './FNOLWizardPage_AX.module.scss';
import metadata from './FNOLWizardPage_AX.metadata.json5';
import FNOLWizardSearchAndSelectPolicyAX from '../../components/FNOLPolicySearchAndSelectComponent_AX/FNOLPolicySearchAndSelectComponent_AX';
import FNOLWizardStepsComponentAX from '../../components/FNOLWizardStepsComponent_AX/FNOLWizardStepsComponent_AX';
import FNOLClaimDetailsComponentAX from '../../components/FNOLClaimDetailsComponent_AX/FNOLClaimDetailsComponent_AX';
import FNOLClaimSummaryComponentAX from '../../components/FNOLClaimSummaryComponent_AX/FNOLClaimSummaryComponent_AX';

function checkLossDate(lossDate, effectiveDate, expirationDate) {
    if (_.isUndefined(lossDate) || _.isUndefined(effectiveDate) || _.isUndefined(expirationDate)) {
        return false;
    }
    const lossDateObject = new Date(lossDate);
    const effectiveDateObject = new Date(effectiveDate.replace('Z', ''));
    const expirationDateObject = new Date(expirationDate.replace('Z', ''));
    expirationDateObject.setHours(23, 59, 59); // to move to the end of day
    effectiveDateObject.setHours(0, 0, 0); // to move to the beginning fo the day
    console.log("effectiveDate", effectiveDateObject);
    console.log("expirationDate", expirationDateObject);
    return (lossDateObject <= new Date())
        && (lossDateObject <= expirationDateObject)
        && (lossDateObject >= effectiveDateObject);
}

function FNOLWizardPageAX() {
    const translator = useTranslator();
    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const [isLoading, setLoadingState] = useState(false);

    const [currentStep, updateCurrentStep] = useState(1);
    const [visitedSteps, updateVisitedSteps] = useState([1]);

    const [policySearchCriteriaVM, setPolicySearchCriteriaVM] = useState({});
    const [selectedPolicySummary, updateSelectedPolicySummary] = useState(undefined);
    const [policySearchResults, updatePolicySearchResults] = useState([]);
    const {
        showAlert
    } = useModal();
    const [detailedPolicy, updateDetailedPolicy] = useState({});
    const [claimReportDTO, updateClaimReportDTO] = useState({ address: '' });
    const [claimSummaryDTO, setClaimSummaryDTO] = useState({});
    const [hasAssitanceCoverage, setHasAssitanceCoverage] = useState(undefined);
    const [claimCreated, setClaimCreated] = useState(false);
    const emailRegexp = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

    useEffect(() => {
        if (!_.isUndefined(viewModelService)) {
            setPolicySearchCriteriaVM(viewModelService.create(
                {
                    lossDate: new Date(),
                    origin_AX: 'autos',
                    country: 'MX'
                },
                'cc',
                'edge.capabilities.claim.fnol.policy.dto.PolicySearchCriteriaDTO'
            ));
            updateClaimReportDTO(viewModelService.create(
                {},
                'cc',
                'edge.capabilities.claim.fnol.dto.AXQuickClaimReportDTO'
            ));
        }
    }, [viewModelService]);

    const onRefreshSearchCriteria = useCallback((newModel) => {
        setPolicySearchCriteriaVM(newModel);
        refreshData();
    }, [refreshData]);

    const onUpdateSearchResults = useCallback((results) => {
        updatePolicySearchResults(results);
        refreshData();
    }, [refreshData]);

    const onSearchFormReset = useCallback(() => {
        setHasAssitanceCoverage(undefined);
        updateCurrentStep(1);
        setClaimCreated(false);
        updateVisitedSteps([1]);
    }, []);

    const onClaimReportModelChanged = useCallback((newModel) => {
        updateClaimReportDTO(newModel);
        refreshData();
    }, [refreshData]);

    const refreshWizardStep = useCallback((newStep) => {
        if (!visitedSteps.includes(newStep)) {
            visitedSteps.push(newStep);
            updateVisitedSteps(visitedSteps);
        }
        updateCurrentStep(newStep);
        refreshData();
    }, [refreshData, visitedSteps]);

    const onEnterStep1 = useCallback(() => {
        if (claimCreated) {
            return false;
        }
        refreshWizardStep(1);
        return true;
    }, [claimCreated, refreshWizardStep]);

    const loadDetailedPolicy = useCallback(async () => {
        let succeed = true;
        setLoadingState(true);
        ClaimService.searchPolicy(selectedPolicySummary, policySearchCriteriaVM.value, authHeader)
            .then((response) => {
                _.set(response, 'policyStatusName', translator({
                    id: `typekey.PolicyStatus.${response.policyStatus}`,
                    defaultMessage: 'N/A'
                }));
                if (!response.isProperty) {
                    _.set(response, 'vehicleInvolved.vehicleTypeName', translator({
                        id: `typekey.VehicleType.${response.vehicleInvolved.vehicleType}`,
                        defaultMessage: 'N/A'
                    }));
                }
                _.set(claimReportDTO, 'lossDate', policySearchCriteriaVM.lossDate.value);
                updateDetailedPolicy(response);
                updateClaimReportDTO(claimReportDTO);
            })
            .catch((error) => {
                succeed = false;
                ToastProvider.toast({
                    message: error.baseError,
                    autoClose: false,
                    type: 'error'
                });
            })
            .finally(() => {
                setLoadingState(false);
            });
        return succeed;
    }, [authHeader, claimReportDTO, policySearchCriteriaVM, selectedPolicySummary, translator]);

    const onEnterStep2 = useCallback(() => {
        if (claimCreated) {
            return false;
        }
        if (_.isUndefined(selectedPolicySummary)) {
            showAlert({
                status: 'error',
                icon: 'error',
                title: translator(messages.missingPolicySelectedErrorTitleMsg),
                message: translator(messages.missingPolicySelectedErrorBodyMsg)
            });
            return false;
        }
        if (!checkLossDate(policySearchCriteriaVM.lossDate.value,
            selectedPolicySummary.effectiveDate, selectedPolicySummary.expirationDate)) {
            showAlert({
                status: 'error',
                icon: 'error',
                title: translator(messages.invalidClaimLossDateTitleMsg),
                message: translator(messages.invalidClaimLossDateBodyMsg)
            });
            return false;
        }
        loadDetailedPolicy();
        refreshWizardStep(2);
        return true;
    }, [claimCreated, policySearchCriteriaVM, refreshWizardStep, selectedPolicySummary,
        translator, loadDetailedPolicy
    ]);

    const hasInvalidField = useCallback((dto) => {
        if (!_.isUndefined(dto.lossDescription.aspects)
            && !dto.lossDescription.aspects.subtreeValid) {
            return true;
        }
        if (!_.isUndefined(dto.accidentType.aspects)
            && !dto.accidentType.aspects.subtreeValid) {
            return true;
        }
        if (detailedPolicy.isProperty && !_.isUndefined(dto.assistance.aspects)
            && !dto.assistance.aspects.subtreeValid) {
            return true;
        }
        // eslint-disable-next-line no-restricted-globals
        if (isNaN(dto.phoneNumber.aspects.typedValue)
        || dto.phoneNumber.aspects.typedValue.replace(/\s/g, '').length < 10) {
            return true;
        }
        if (!dto.email.aspects.subtreeValid && !emailRegexp.test(dto.email.aspects.typedValue)) {
            return true;
        }
        return false;
    }, [detailedPolicy.isProperty]);

    const blockCreateClaim = ((accidenType) => {
        if (_.isUndefined(accidenType)) {
            return false;
        }
        const specialConditionSelected = _.find(detailedPolicy.assistances, {
            accidentType: accidenType.code
        });
        if (specialConditionSelected) {
            return specialConditionSelected.blockClaim;
        }
        return false;
    });

    const onEnterStep3 = useCallback(() => {
        if (claimCreated) {
            refreshWizardStep(3);
            return true;
        }
        if (blockCreateClaim(claimReportDTO.accidentType.value)) {
            showAlert({
                status: 'error',
                icon: 'error',
                title: translator(messages.invalidSendClaimTitle),
                message: translator(messages.invalidAssistance)
            });
            return false;
        }
        if (hasAssitanceCoverage !== undefined && !hasAssitanceCoverage) {
            showAlert({
                status: 'error',
                icon: 'error',
                title: translator(messages.noAssistanceCoverageError),
                message: translator(messages.noAssistanceCoverageErrorMessage)
            });
            return false;
        }
        const invalidField = hasInvalidField(claimReportDTO);
        if (invalidField) {
            showAlert({
                status: 'error',
                icon: 'error',
                title: translator(messages.invalidSendClaimTitle),
                message: translator(messages.invalidSendClaim)
            });
            return false;
        }
        refreshWizardStep(3);
        return true;
    }, [claimCreated, claimReportDTO, hasAssitanceCoverage,
        hasInvalidField, refreshWizardStep, translator]);

    const onPolicySelected = useCallback((policySelected) => {
        updateSelectedPolicySummary(policySelected);
    }, []);

    const override = {
        stepOneComponent: {
            visible: currentStep === 1,
            setPolicySelected: onPolicySelected,
            policySearchCriteriaVM: policySearchCriteriaVM,
            onUpdateModel: onRefreshSearchCriteria,
            policiesList: policySearchResults,
            updateSearchResults: onUpdateSearchResults,
            formReset: onSearchFormReset,
            setClaimCreated: setClaimCreated,
            currentStep: currentStep
        },
        stepTwoComponent: {
            visible: currentStep === 2,
            policySelected: selectedPolicySummary,
            policySearchCriteriaVM: policySearchCriteriaVM,
            fullPolicy: detailedPolicy,
            claimReportDTO: claimReportDTO,
            onRefreshClaimModel: onClaimReportModelChanged,
            hasAssitenceCoverage: hasAssitanceCoverage,
            setHasAssitenceCoverage: setHasAssitanceCoverage,
        },
        stepThreeComponent: {
            visible: currentStep === 3,
            claimReportDTO: claimReportDTO,
            fullPolicy: detailedPolicy,
            claimCreated: claimCreated,
            setClaimSummaryDTO: setClaimSummaryDTO,
            claimSummaryDTO: claimSummaryDTO,
            setClaimCreated: setClaimCreated
        },
        wizardSteps: {
            onEnterStep1: onEnterStep1,
            onEnterStep2: onEnterStep2,
            onEnterStep3: onEnterStep3,
            currentStep: currentStep,
            claimCreated: claimCreated
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
        },
        resolveComponentMap: {
            fnolWizarStep1Component: FNOLWizardSearchAndSelectPolicyAX,
            fnolWizarStep2Component: FNOLClaimDetailsComponentAX,
            fnolWizarStep3Component: FNOLClaimSummaryComponentAX,
            stepsComponent: FNOLWizardStepsComponentAX
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }
    return renderContentFromMetadataInternal(metadata.pageContent, override, resolvers);

}

export default withRouter(FNOLWizardPageAX);
